import { useLocalSearchParams } from 'expo-router';
import { useMutation } from 'urql';
import EventClock from '@/components/Event/EventClock';
import { EventClockFragment } from '@/components/Event/EventClock/EventClock.utils';
import Box from '@/components/UI/Box';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import EventScorekeepingPanelTimerPlayPause, {
  EventScorekeepingPanelTimerPlayPauseFragment,
} from './EventScorekeepingPanelTimerPlayPause';
import {
  EventScorekeepingPanelTimerSegment,
  EventScorekeepingPanelTimerSegmentFragment,
} from './EventScorekeepingPanelTimerSegment';

const UpdateEventClock = graphql(/* GraphQL */ `
  mutation UpdateEventClock($eventId: uuid!, $elapsedSegmentMs: Int!) {
    update_event_clock_by_pk(
      pk_columns: { event_id: $eventId }
      _set: { elapsed_segment_ms: $elapsedSegmentMs }
    ) {
      event_id
      event {
        id
        ...EventClock
      }
    }
  }
`, [EventClockFragment]);

export const EventScorekeepingPanelTimerFragment = graphql(/* GraphQL */ `
  fragment EventScorekeepingPanelTimer on event {
    event_segment_group {
      is_timed
    }
    event_clock {
      event_id
    }
    ...EventScorekeepingPanelTimerSegment
    ...EventScorekeepingPanelTimerPlayPause
    ...EventClock
  }
`, [
  EventScorekeepingPanelTimerSegmentFragment,
  EventScorekeepingPanelTimerPlayPauseFragment,
  EventClockFragment,
]);

export default function EventScorekeepingPanelTimer({
  fragment,
}: {
  fragment:
    | FragmentOf<typeof EventScorekeepingPanelTimerFragment>
    | null
    | undefined;
}) {
  const [, updateEventClock] = useMutation(UpdateEventClock);

  const event = readFragment(EventScorekeepingPanelTimerFragment, fragment);
  const { eventId } = useLocalSearchParams<{ eventId?: string }>();

  const onChangeClock = async (actualElapsedMs: number) => {
    if (!eventId) {
      return;
    }

    await updateEventClock({
      eventId,
      elapsedSegmentMs: actualElapsedMs,
    });
  };

  return (
    <Box direction="horizontal" center space="lg">
      {event?.event_clock?.event_id ? (
        <EventScorekeepingPanelTimerSegment fragment={event} />
      ) : null}
      {event?.event_segment_group?.is_timed ? (
        <EventScorekeepingPanelTimerPlayPause fragment={event} />
      ) : null}
      {event?.event_segment_group?.is_timed && event.event_clock?.event_id ? (
        <EventClock fragment={event} onChange={onChangeClock} />
      ) : null}
    </Box>
  );
}
